import type { ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router';

import { Tooltip } from '@/components/Tooltip/Tooltip';

import type { CourseComponentItem } from './useCourse';
import { formatNumberedTitle } from './courseHomeUtils';
import styles from './ComponentCard.module.css';

interface ComponentCardProps {
  actions?: ReactNode;
  displayNumber?: number | null;
  dragHandle?: ReactNode;
  item: CourseComponentItem;
  showStatusVisibilityIndicator?: boolean;
}

function getStatusVisibility(statusValue: string) {
  return statusValue === 'draft' || statusValue === 'scheduled'
    ? {
        Icon: EyeOff,
        tooltip: 'Not visible to students',
      }
    : {
        Icon: Eye,
        tooltip: 'Visible to students',
      };
}

export function ComponentCard({
  actions,
  displayNumber = null,
  dragHandle,
  item,
  showStatusVisibilityIndicator = false,
}: ComponentCardProps) {
  const statusValue = item.statusLabel.toLowerCase();
  const { Icon: StatusIcon, tooltip: statusTooltip } =
    getStatusVisibility(statusValue);
  const title = formatNumberedTitle(item.title, displayNumber);
  const metaLabels = [
    item.dateLabel,
    item.dueLabel,
    item.preLectureLabel,
    item.visibilityLabel,
  ].filter((label): label is string => Boolean(label));

  return (
    <article
      className={styles.card}
      data-has-actions={actions ? 'true' : 'false'}
      data-has-drag-handle={dragHandle ? 'true' : 'false'}
      data-has-subtitle={metaLabels.length > 0 ? 'true' : 'false'}
      data-status={statusValue}
    >
      {dragHandle ? <div className={styles.dragSlot}>{dragHandle}</div> : null}
      <Link className={styles.primaryLink} to={item.href}>
        <div className={styles.textBlock}>
          <h2 className={styles.title}>{title}</h2>
          {metaLabels.length > 0 ? (
            <p className={styles.subtitle}>
              {/* The spaces sit outside the hidden dot, or a screen reader
                  runs the labels together (#173). */}
              {metaLabels.map((label, index) => (
                <span key={`${label}-${index}`}>
                  {index > 0 ? (
                    <>
                      {' '}
                      <span aria-hidden="true">·</span>{' '}
                    </>
                  ) : null}
                  <span>{label}</span>
                </span>
              ))}
            </p>
          ) : null}
        </div>
        {showStatusVisibilityIndicator ? (
          <Tooltip content={statusTooltip}>
            <span
              aria-label={`${item.statusLabel}: ${statusTooltip}`}
              className={styles.status}
              data-status={statusValue}
            >
              <StatusIcon aria-hidden="true" size={12} strokeWidth={2.2} />
              <span>{item.statusLabel}</span>
            </span>
          </Tooltip>
        ) : (
          <span className={styles.status} data-status={statusValue}>
            {item.statusLabel}
          </span>
        )}
      </Link>
      {actions ? <div className={styles.actionsSlot}>{actions}</div> : null}
    </article>
  );
}
